// R1b-α → R1c dead-click fix F1 — Portal Switcher launcher.
// Owner-only chip in the AdminAppShell top bar. Menu items now START
// impersonation instantly (one click) instead of navigating to the picker
// page. "Manage impersonation…" fallback still routes to the picker for
// per-user targeting.
import React, { useState, useRef, useEffect } from "react";
import { UserCog, ChevronRight, Palette, Settings2 } from "lucide-react";

// LIVE role targets → real role_key values the backend expects on
// POST /api/portal/impersonate/start (payload `{target: "role:<role_key>"}`).
// Grayed entries stay honest with truthful "Coming in R2/R3" tags.
const PORTALS = [
  { key: "OWNER_SUPER_ADMIN", label: "Owner",        status: "live",   phase: null },
  { key: "ADMINISTRATOR",     label: "Administrator",status: "live",   phase: null },
  { key: "CLIENT",            label: "Client",       status: "live",   phase: null },
  { key: "estimator",         label: "Estimator",    status: "grayed", phase: "R3" },
  { key: "project-ops",       label: "Project Ops",  status: "grayed", phase: "R2" },
  { key: "field-ops",         label: "Field Ops",    status: "grayed", phase: "R3" },
  { key: "professional",      label: "Professional", status: "grayed", phase: "R3" },
];

/**
 * @param {Object} props
 * @param {boolean} props.isOwner — non-owners see nothing.
 * @param {(roleKey:string)=>void} [props.onImpersonate] — one-click impersonation start.
 * @param {()=>void} [props.onOpenPortalSwitch] — fallback: routes to /admin/portal-switch.
 * @param {()=>void} [props.onOpenDesignLibrary] — deep-link to /admin/design-library-preview.
 */
export function PortalSwitcherLauncher({ isOwner, onImpersonate, onOpenPortalSwitch, onOpenDesignLibrary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!isOwner) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="ds-shell-icon"
        aria-label="Portal switcher — view as role"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="portal-switcher-launcher"
      >
        <UserCog size={18} />
      </button>
      {open && (
        <div
          role="menu"
          aria-live="polite"
          data-testid="portal-switcher-menu"
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            minWidth: 260, background: "var(--color-surface)",
            border: "1px solid var(--color-border)", borderRadius: 10,
            boxShadow: "0 12px 40px rgba(0,0,0,0.45)", padding: 6, zIndex: 55,
          }}
        >
          <div style={{
            fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "var(--color-text-muted)",
            padding: "8px 12px 4px",
          }}>View as role</div>
          {PORTALS.map((p) => {
            const isLive = p.status === "live";
            return (
              <button
                key={p.key}
                role="menuitem"
                type="button"
                disabled={!isLive}
                title={!isLive ? `Coming in ${p.phase}` : `Start impersonating as ${p.label}`}
                aria-label={isLive ? `Impersonate as ${p.label}` : `${p.label} — coming in ${p.phase}`}
                data-testid={`portal-switcher-role-${p.key}`}
                onClick={() => {
                  if (!isLive) return;
                  setOpen(false);
                  onImpersonate?.(p.key);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "9px 12px", background: "transparent", border: 0,
                  fontFamily: "var(--font-body)", fontSize: 13, textAlign: "left",
                  color: isLive ? "var(--color-text)" : "var(--color-text-muted)",
                  cursor: isLive ? "pointer" : "not-allowed",
                  opacity: isLive ? 1 : 0.5, borderRadius: 6,
                }}
                onMouseEnter={(e) => isLive && (e.currentTarget.style.background = "var(--color-surface-2)")}
                onMouseLeave={(e) => isLive && (e.currentTarget.style.background = "transparent")}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: isLive ? "var(--color-status-positive)" : "var(--color-text-muted)",
                }} />
                <span style={{ flex: 1 }}>{p.label}</span>
                {!isLive && (
                  <span style={{
                    fontFamily: "var(--font-mono)", fontSize: 9,
                    letterSpacing: "0.12em", color: "var(--color-status-info)",
                  }}>{p.phase}</span>
                )}
                {isLive && <ChevronRight size={12} style={{ opacity: 0.5 }} />}
              </button>
            );
          })}
          <div style={{ height: 1, background: "var(--color-border)", margin: "6px 8px" }} />
          <button
            role="menuitem"
            type="button"
            data-testid="portal-switcher-manage-link"
            onClick={() => { setOpen(false); onOpenPortalSwitch?.(); }}
            title="Open the full impersonation picker (per-user targeting)"
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "9px 12px", background: "transparent", border: 0,
              fontFamily: "var(--font-body)", fontSize: 13, textAlign: "left",
              color: "var(--color-text)", cursor: "pointer", borderRadius: 6,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-surface-2)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <Settings2 size={14} />
            <span style={{ flex: 1 }}>Manage impersonation…</span>
            <ChevronRight size={12} style={{ opacity: 0.5 }} />
          </button>
          {onOpenDesignLibrary && (
          <button
            role="menuitem"
            type="button"
            data-testid="portal-switcher-design-library"
            onClick={() => { setOpen(false); onOpenDesignLibrary?.(); }}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "9px 12px", background: "transparent", border: 0,
              fontFamily: "var(--font-body)", fontSize: 13, textAlign: "left",
              color: "var(--color-primary)", cursor: "pointer", borderRadius: 6,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-surface-2)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <Palette size={14} />
            <span style={{ flex: 1 }}>Design library preview</span>
            <ChevronRight size={12} style={{ opacity: 0.5 }} />
          </button>
          )}
        </div>
      )}
    </div>
  );
}
